import { canAccessBusiness, canAccessInvoice, canUseAccountingOperation } from "../accessControl";
import { BANK_TRANSACTION_CATEGORY_CODES, bankTransactionCategory } from "../banking/categories";
import { classifyAllocation, confirmedAllocations } from "../banking/reconciliation";
import { ACCOUNTING_EVIDENCE_DOCUMENT_TYPES } from "../documentIntake";
import { invoiceNumber, parseCurrency } from "../invoiceUtils";

export const BANK_EVIDENCE_STATUSES = ["Missing evidence", "Requested", "Personal / non-deductible", "Reviewed"];
export const BANK_REVIEW_STATES = Object.freeze(['unreviewed', 'waiting_for_evidence', 'reviewed_without_invoice']);

export const PROTOTYPE_BANKING_MUTATION_CAPABILITIES = Object.freeze({
  mode: "prototype",
  productionReady: false,
  organizationGuard: true,
  managedBusinessGuard: true,
  operationGrant: "banking",
  recomputesAllocations: true,
  auditPlan: true,
  serverTransactional: false,
});

const denied = (error = "You do not have access to that managed business.") => ({ ok: false, error });
const publicModeDenied = () => denied("Banking stays read-only in public mode");

const validBusinessScope = ({ business, organizationId, profile }) => Boolean(
  business
  && business.workspaceId === organizationId
  && canAccessBusiness(profile, business.id)
  && canUseAccountingOperation(profile, "banking")
);

const validTransactionScope = ({ transaction, business, organizationId, profile }) => Boolean(
  transaction
  && transaction.workspaceId === organizationId
  && transaction.businessId === business?.id
  && validBusinessScope({ business, organizationId, profile })
);

const validInvoiceScope = ({ invoice, business, organizationId, profile }) => Boolean(
  invoice
  && invoice.workspaceId === organizationId
  && invoice.issuerId === business?.id
  && canAccessInvoice(profile, invoice)
  && validBusinessScope({ business, organizationId, profile })
);

const invoiceCurrency = (invoice) => invoice.currency || String(invoice.amount || "EUR").match(/\b[A-Z]{3}\b/)?.[0] || "EUR";
const scopedTransactions = (transactions, organizationId, businessId) => transactions.filter((transaction) => transaction.workspaceId === organizationId && transaction.businessId === businessId);
const updateTransaction = (transactions, id, nextTransaction) => transactions.map((transaction) => transaction.id === id ? nextTransaction : transaction);

const statusFromAllocations = (invoice, allocationTotals, fallback) => {
  const allocated = allocationTotals[invoice.id] || 0;
  const total = parseCurrency(invoice.amount);
  if (allocated >= total - 0.005) return "Paid";
  if (allocated > 0) return "Partially paid";
  return fallback || invoice.status;
};

const invoiceWithStatus = (invoice, invoiceStatuses = {}) => ({ ...invoice, status: invoiceStatuses[invoice.id] || invoice.status });

export const planBankCategoryUpdate = ({
  transaction,
  transactions = [],
  categoryCode,
  business,
  organizationId,
  profile,
  publicMode = false,
  now = new Date().toISOString(),
}) => {
  if (!validTransactionScope({ transaction, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (!BANK_TRANSACTION_CATEGORY_CODES.includes(categoryCode)) return denied("Choose a supported bank transaction category.");
  const current = bankTransactionCategory(transaction);
  if (current.code === categoryCode && current.isManual) return { ok: true, noop: true };
  const nextTransaction = {
    ...transaction,
    revision: (transaction.revision || 1) + 1,
    categoryCode,
    categorySource: "manual",
    categoryConfidence: null,
    categoryRuleKey: null,
    categoryUpdatedAt: now,
    categoryUpdatedBy: profile.name,
    categoryHistory: [...(transaction.categoryHistory || []), { before: current.code, after: categoryCode, actorId: profile.id, at: now, revision: (transaction.revision || 1) + 1 }],
  };
  const nextCategory = bankTransactionCategory(nextTransaction);
  return {
    ok: true,
    transactions: updateTransaction(transactions, transaction.id, nextTransaction),
    transaction: nextTransaction,
    toast: `Category changed to ${nextCategory.label}`,
    audit: {
      action: "Bank transaction categorized",
      detail: `Changed ${current.label} to ${nextCategory.label}`,
      entityType: "bank-transaction",
      entityId: transaction.id,
    },
  };
};

export const planBankReconciliationConfirm = ({
  transaction,
  transactions = [],
  invoices = [],
  invoiceStatuses = {},
  invoiceIds = [],
  bankFeeAmount = 0,
  business,
  organizationId,
  profile,
  publicMode = false,
  now = new Date().toISOString(),
}) => {
  if (!validTransactionScope({ transaction, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (!(Number(transaction.signedAmount) > 0)) return denied("Only incoming payments can be matched to sales invoices.");
  if (transaction.reconciliation?.confirmedAt || transaction.reconciliationStatus === "No match") {
    return denied("Reopen this transaction before confirming another outcome.");
  }

  const uniqueInvoiceIds = [...new Set(invoiceIds)];
  if (!uniqueInvoiceIds.length || uniqueInvoiceIds.length !== invoiceIds.length) return denied("Choose one or more unique invoice records.");
  const selected = uniqueInvoiceIds.map((id) => invoices.find((invoice) => invoice.id === id));
  if (selected.some((invoice) => !validInvoiceScope({ invoice, business, organizationId, profile }))) return denied();
  const currentInvoices = selected.map((invoice) => invoiceWithStatus(invoice, invoiceStatuses));
  if (currentInvoices.some((invoice) => invoice.status === "Draft" || invoice.archived)) {
    return denied("Draft or archived invoices cannot receive bank allocations.");
  }
  if (currentInvoices.some((invoice) => invoiceCurrency(invoice) !== transaction.currency)) {
    return denied("The payment and selected invoices must use the same currency.");
  }

  const businessTransactions = scopedTransactions(transactions, organizationId, business.id);
  const allocation = classifyAllocation(transaction, uniqueInvoiceIds, currentInvoices, businessTransactions, bankFeeAmount);
  if (allocation.classification === "No match" || !allocation.allocations.length || allocation.invoiceIds.length !== uniqueInvoiceIds.length) {
    return denied("The selected invoices do not produce a valid allocation.");
  }
  const previousStatuses = Object.fromEntries(currentInvoices.map((invoice) => [invoice.id, invoice.status || "Sent"]));
  const reconciliation = { ...allocation, confirmedAt: now, confirmedBy: profile.name, previousStatuses };
  const nextTransaction = { ...transaction, reconciliationStatus: allocation.classification, reconciliation };
  const nextTransactions = updateTransaction(transactions, transaction.id, nextTransaction);
  const nextBusinessTransactions = scopedTransactions(nextTransactions, organizationId, business.id);
  const allocationTotals = confirmedAllocations(nextBusinessTransactions);
  const nextInvoiceStatuses = { ...invoiceStatuses };
  currentInvoices.forEach((invoice) => {
    nextInvoiceStatuses[invoice.id] = statusFromAllocations(invoice, allocationTotals, invoice.status);
  });
  const numbers = currentInvoices.map(invoiceNumber).join(", ");
  return {
    ok: true,
    transactions: nextTransactions,
    invoiceStatuses: nextInvoiceStatuses,
    transaction: nextTransaction,
    allocation,
    toast: `${allocation.classification} confirmed`,
    audit: {
      action: "Bank payment reconciled",
      detail: `${allocation.classification} confirmed against ${numbers}${allocation.bankFeeAmount > 0 ? ` with ${allocation.bankFeeAmount.toFixed(2)} ${transaction.currency} recorded as a bank fee` : ""}`,
      entityType: "bank-transaction",
      entityId: transaction.id,
    },
  };
};

export const planBankNoMatch = ({
  transaction,
  transactions = [],
  business,
  organizationId,
  profile,
  publicMode = false,
  now = new Date().toISOString(),
}) => {
  if (!validTransactionScope({ transaction, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (!(Number(transaction.signedAmount) > 0)) return denied("Only incoming payments use invoice match outcomes.");
  if (transaction.reconciliation?.confirmedAt) return denied("Reopen the confirmed match before recording no match.");
  if (transaction.reconciliationStatus === "No match") return { ok: true, noop: true };
  const nextTransaction = { ...transaction, reconciliationStatus: "No match", reconciliation: null, reviewedAt: now, reviewedBy: profile.name };
  return {
    ok: true,
    transactions: updateTransaction(transactions, transaction.id, nextTransaction),
    transaction: nextTransaction,
    toast: "Transaction recorded with no invoice match",
    audit: {
      action: "Bank transaction reviewed",
      detail: "Recorded no matching invoice",
      entityType: "bank-transaction",
      entityId: transaction.id,
    },
  };
};

export const planBankReconciliationReopen = ({
  transaction,
  transactions = [],
  invoices = [],
  invoiceStatuses = {},
  business,
  organizationId,
  profile,
  publicMode = false,
}) => {
  if (!validTransactionScope({ transaction, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (!transaction.reconciliation?.confirmedAt && transaction.reconciliationStatus !== "No match") return { ok: true, noop: true };

  const previous = transaction.reconciliation?.previousStatuses || {};
  const invoiceIds = transaction.reconciliation?.invoiceIds || [];
  const affectedInvoices = invoiceIds.map((id) => invoices.find((invoice) => invoice.id === id));
  if (affectedInvoices.some((invoice) => !validInvoiceScope({ invoice, business, organizationId, profile }))) return denied();

  const nextTransaction = { ...transaction, reconciliationStatus: "Unmatched", reconciliation: null, reviewedAt: null, reviewedBy: null };
  const nextTransactions = updateTransaction(transactions, transaction.id, nextTransaction);
  const allocationTotals = confirmedAllocations(scopedTransactions(nextTransactions, organizationId, business.id));
  const nextInvoiceStatuses = { ...invoiceStatuses };
  affectedInvoices.forEach((invoice) => {
    const current = invoiceWithStatus(invoice, invoiceStatuses);
    nextInvoiceStatuses[invoice.id] = statusFromAllocations(current, allocationTotals, previous[invoice.id]);
  });
  return {
    ok: true,
    transactions: nextTransactions,
    invoiceStatuses: nextInvoiceStatuses,
    transaction: nextTransaction,
    toast: "Transaction returned to review",
    audit: {
      action: "Bank reconciliation reopened",
      detail: "Returned the transaction to accountant review",
      entityType: "bank-transaction",
      entityId: transaction.id,
    },
  };
};

const EVIDENCE_UPDATE_FIELDS = new Set(["evidenceStatus", "evidenceOwnerId", "evidenceDeadline"]);

const validateEvidenceUpdate = ({ transaction, changes, business, organizationId, profile, publicMode }) => {
  if (!validTransactionScope({ transaction, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (!(Number(transaction.signedAmount) < 0)) return denied("Evidence follow-up is available only for outgoing payments.");
  if (!changes || typeof changes !== "object" || Array.isArray(changes)
    || !Object.keys(changes).length || Object.keys(changes).some((field) => !EVIDENCE_UPDATE_FIELDS.has(field))) return denied("Choose a supported evidence follow-up field.");
  if (Object.hasOwn(changes, "evidenceStatus") && !BANK_EVIDENCE_STATUSES.includes(changes.evidenceStatus)) return denied("Choose a supported evidence status.");
  if (changes.evidenceDeadline && (!/^\d{4}-\d{2}-\d{2}$/.test(changes.evidenceDeadline)
    || !Number.isFinite(Date.parse(changes.evidenceDeadline))
    || new Date(changes.evidenceDeadline).toISOString().slice(0, 10) !== changes.evidenceDeadline)) return denied("Choose a valid follow-up date.");
  if (transaction.evidenceMatch?.confirmedAt && changes.evidenceStatus && changes.evidenceStatus !== "Evidence linked") {
    return denied("Unlink the supporting document before changing its evidence state.");
  }
  return null;
};

// Account commands use the live database grant check, not the bounded local team roster.
export const prepareAccountBankEvidenceUpdate = (options) => {
  const invalid = validateEvidenceUpdate(options);
  if (invalid) return invalid;
  const next = { ...options.transaction, ...options.changes };
  if (next.evidenceOwnerId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(next.evidenceOwnerId)) {
    return denied("Choose an eligible Banking team member.");
  }
  return { ok: true, followup: { status: next.evidenceStatus || "Missing evidence", ownerId: next.evidenceOwnerId || null, deadline: next.evidenceDeadline || null } };
};

export const planBankEvidenceUpdate = ({
  transaction,
  transactions = [],
  changes = {},
  members = [],
  business,
  organizationId,
  profile,
  publicMode = false,
  now = new Date().toISOString(),
}) => {
  const invalid = validateEvidenceUpdate({ transaction, changes, business, organizationId, profile, publicMode });
  if (invalid) return invalid;
  if (changes.evidenceOwnerId) {
    const owner = members.find((member) => member.id === changes.evidenceOwnerId);
    if (!owner || owner.workspaceId !== organizationId || !canAccessBusiness(owner, business.id) || !canUseAccountingOperation(owner, "banking")) {
      return denied("Assign this follow-up to an active team member with Banking access.");
    }
  }
  const nextTransaction = { ...transaction, ...changes, evidenceUpdatedAt: now, evidenceUpdatedBy: profile.name };
  if (JSON.stringify(nextTransaction) === JSON.stringify(transaction)) return { ok: true, noop: true };
  const field = Object.keys(changes)[0] || "evidence follow-up";
  return {
    ok: true,
    transactions: updateTransaction(transactions, transaction.id, nextTransaction),
    transaction: nextTransaction,
    audit: {
      action: "Evidence follow-up updated",
      detail: `Changed ${field} for an outgoing bank transaction`,
      entityType: "bank-transaction",
      entityId: transaction.id,
    },
  };
};

export const planOutgoingEvidenceLink = ({
  transaction,
  evidence,
  transactions = [],
  business,
  organizationId,
  profile,
  publicMode = false,
  now = new Date().toISOString(),
}) => {
  if (!validTransactionScope({ transaction, business, organizationId, profile }) || !Number.isFinite(Number(transaction?.signedAmount)) || Number(transaction.signedAmount) === 0) return denied();
  if (publicMode) return publicModeDenied();
  if (!evidence
    || evidence.workspaceId !== organizationId
    || evidence.businessId !== business.id
    || evidence.status !== "Accepted"
    || !ACCOUNTING_EVIDENCE_DOCUMENT_TYPES.includes(evidence.documentType)) {
    return denied("Choose an accepted purchase invoice or receipt from this managed business");
  }
  if (evidence.accountingEvidence?.currency && evidence.accountingEvidence.currency !== transaction.currency) {
    return denied("The supporting document and bank payment must use the same currency.");
  }
  if (transaction.evidenceMatch?.confirmedAt) return denied("Unlink the current supporting document before choosing another.");
  const alreadyLinked = transactions.find((candidate) => candidate.id !== transaction.id
    && candidate.evidenceMatch?.confirmedAt
    && candidate.evidenceMatch.intakeId === evidence.id);
  if (alreadyLinked) return denied("That document is already linked to another bank transaction");

  const evidenceMatch = {
    intakeId: evidence.id,
    documentType: evidence.documentType,
    confirmedAt: now,
    confirmedBy: profile.name,
    previousEvidenceStatus: transaction.evidenceStatus || "Missing evidence",
  };
  const nextTransaction = {
    ...transaction,
    evidenceStatus: "Evidence linked",
    reconciliationStatus: "Evidence linked",
    evidenceMatch,
    evidenceUpdatedAt: now,
    evidenceUpdatedBy: profile.name,
  };
  return {
    ok: true,
    transactions: updateTransaction(transactions, transaction.id, nextTransaction),
    transaction: nextTransaction,
    toast: `${evidence.documentType} linked as supporting evidence`,
    audit: {
      action: "Outgoing evidence linked",
      detail: `Linked an accepted ${evidence.documentType.toLowerCase()} to the bank transaction`,
      entityType: "bank-transaction",
      entityId: transaction.id,
    },
  };
};

export const planOutgoingEvidenceUnlink = ({
  transaction,
  transactions = [],
  business,
  organizationId,
  profile,
  publicMode = false,
  now = new Date().toISOString(),
}) => {
  if (!validTransactionScope({ transaction, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (!transaction.evidenceMatch?.confirmedAt) return { ok: true, noop: true };
  const previousEvidenceStatus = transaction.evidenceMatch.previousEvidenceStatus || "Missing evidence";
  const nextTransaction = {
    ...transaction,
    evidenceStatus: previousEvidenceStatus,
    reconciliationStatus: "Unmatched",
    evidenceMatch: null,
    evidenceUpdatedAt: now,
    evidenceUpdatedBy: profile.name,
  };
  return {
    ok: true,
    transactions: updateTransaction(transactions, transaction.id, nextTransaction),
    transaction: nextTransaction,
    toast: "Supporting document returned to review",
    audit: {
      action: "Outgoing evidence unlinked",
      detail: "Returned the bank transaction to supporting-document review",
      entityType: "bank-transaction",
      entityId: transaction.id,
    },
  };
};

export const planBankReviewDecision = ({
  transaction, transactions = [], business, organizationId, profile, publicMode = false,
  state, reason, expectedRevision, idempotencyKey, now = new Date().toISOString(),
}) => {
  if (publicMode) return publicModeDenied();
  if ((profile?.workspaceId && profile.workspaceId !== organizationId) || profile?.status !== 'Active' || !profile?.id
    || business?.archived || business?.archivedAt) return denied();
  const matches = transactions.filter(item => item.id === transaction?.id);
  const current = matches[0];
  if (matches.length !== 1 || !validTransactionScope({ transaction: current, business, organizationId, profile })) return denied();
  if (!Number.isFinite(Number(current.signedAmount)) || Number(current.signedAmount) === 0) return denied('Choose an incoming or outgoing bank movement.');
  if (!BANK_REVIEW_STATES.includes(state) || typeof reason !== 'string' || [...reason.trim()].length > 2000) {
    return denied('Choose a review state; the optional comment can contain up to 2000 characters.');
  }
  if (!Number.isInteger(expectedRevision) || expectedRevision < 1 || expectedRevision >= 2147483647
    || typeof idempotencyKey !== 'string' || !/^[A-Za-z0-9._:-]{16,128}$/.test(idempotencyKey)
    || typeof now !== 'string' || !Number.isFinite(Date.parse(now))) return denied('The original review request could not be verified.');
  const request = { organizationId, businessId: business.id, transactionId: current.id,
    actorId: profile.id, state, reason: reason.trim(), expectedRevision };
  const history = current.bankReviewHistory || [];
  if (!Array.isArray(history)) return denied('The saved review history could not be verified.');
  const prior = history.find(event => event.idempotencyKey === idempotencyKey);
  if (prior) {
    if (JSON.stringify(prior.request) !== JSON.stringify(request) || !prior.receipt) return denied('This review key belongs to a different request.');
    return { ok: true, noop: true, replayed: true, receipt: prior.receipt };
  }
  const revision = current.revision ?? 1;
  if (revision !== expectedRevision) return { ...denied('This bank transaction changed. Reload before saving the review.'), code: 'resource_revision_conflict' };
  const previous = current.bankReview || { state: 'unreviewed', reason: null, actorId: null, at: null };
  if (!BANK_REVIEW_STATES.includes(previous.state)) return denied('The previous review state cannot be verified.');
  if (state === 'reviewed_without_invoice' && (current.evidenceMatch?.confirmedAt
    || (current.reconciliation?.confirmedAt && current.reconciliationStatus !== 'No match'))) {
    return denied('A linked or allocated transaction cannot be reviewed without an invoice.');
  }
  const review = { state, reason: request.reason, actorId: profile.id, at: now };
  const receipt = { organizationId, businessId: business.id, transactionId: current.id, revision: revision + 1, review };
  const before = { organizationId, businessId: business.id, transactionId: current.id, revision, review: previous };
  const next = { ...current, revision: revision + 1, bankReview: review,
    bankReviewHistory: [...history, { idempotencyKey, request, before, receipt }] };
  return { ok: true, receipt, transaction: next, transactions: updateTransaction(transactions, current.id, next),
    audit: { action: 'Bank accountant review changed', entityType: 'bank-review', entityId: current.id,
      detail: request.reason, actorId: profile.id, at: now, before, after: receipt } };
};

export const bankingMutationRepository = Object.freeze({
  review: planBankReviewDecision,
  capabilities: PROTOTYPE_BANKING_MUTATION_CAPABILITIES,
  confirmReconciliation: planBankReconciliationConfirm,
  markNoMatch: planBankNoMatch,
  reopenReconciliation: planBankReconciliationReopen,
  updateEvidence: planBankEvidenceUpdate,
  updateCategory: planBankCategoryUpdate,
  linkEvidence: planOutgoingEvidenceLink,
  unlinkEvidence: planOutgoingEvidenceUnlink,
});
